import { useCallback, useEffect, useState } from 'react'
import { useLocation } from 'react-router-dom'

import { Results, TrainingTestOutcome } from '../../types'
import { SortingDirection } from '../../types/sorting'
import { get } from '../../utils/api'
import { handleApiErrors } from '../../utils/apiErrors'
import { mergeResults } from '../../utils/pagination'
import { getQueryString } from '../../utils/queryString'
import { serializeSorting } from '../../utils/sorting'

type Columns = TrainingTestOutcome

type Sorting = { [key in keyof Partial<Columns>]: SortingDirection }
type Filtering = {
  [key in keyof Partial<Columns>]: string | number | boolean | undefined
}

export const useFetchTestOutcomes = ({
  trainingTestId,
  filtering,
  sorting,
}: {
  trainingTestId: number
  filtering: Filtering
  sorting: Sorting
}) => {
  const [isFetching, setFetching] = useState(false)
  const [testOutcomes, setTestOutcomes] = useState<
    Results<TrainingTestOutcome> | undefined
  >()

  const limit = 20
  const [offset, setOffset] = useState(0)
  const location = useLocation()

  const loadTestOutcomes = useCallback(async () => {
    if (isFetching) return // prevent multiple requests

    setFetching(true)

    const queryParams = new URLSearchParams(location.search)
    const simulationMode = queryParams.get('simulation_mode')
    const threshold = queryParams.get('threshold')
    const prediction = queryParams.get('prediction')
    const expectation = queryParams.get('expectation')

    let queryString = getQueryString({
      trainingTest: trainingTestId,
      limit,
      offset,
      ...filtering,
      ordering: serializeSorting(sorting),
    })

    if (simulationMode && threshold) {
      queryString = getQueryString({
        trainingTest: trainingTestId,
        limit,
        offset,
        ...filtering,
        ordering: serializeSorting(sorting),
        simulation_mode: simulationMode,
        threshold,
      })
    }

    // Append query params from ConfusionMatrix
    if (prediction) {
      queryString += `&${getQueryString({ prediction })}`
    }
    if (expectation) {
      queryString += `&${getQueryString({ expectation })}`
    }

    get<Results<TrainingTestOutcome>>(`/training_test_outcomes/?${queryString}`)
      .then((response) => {
        setTestOutcomes(mergeResults(response))
        setOffset((prev) => prev + limit)
      })
      .catch(handleApiErrors)
      .finally(() => setFetching(false))
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isFetching, offset, trainingTestId, filtering, sorting])

  useEffect(() => {
    setTestOutcomes(undefined)
    setOffset(0)
  }, [filtering, sorting, location.key])

  return {
    testOutcomes: testOutcomes ? testOutcomes.results : [],
    count: testOutcomes ? testOutcomes.count : 0,
    isFetching,
    isEmpty: !isFetching && testOutcomes?.results.length === 0,
    hasMore: testOutcomes
      ? testOutcomes.results.length < testOutcomes.count
      : true,
    loadTestOutcomes,
  }
}
